import { Button, ButtonGroup, Checkbox, FormControlLabel, FormLabel, InputAdornment, makeStyles, OutlinedInput, TextareaAutosize } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isReelsFeedEnabled } from '../StoryFeed/story-feed-helper';
import { ArrowBack, Close } from '@material-ui/icons';
import tinycolor from 'tinycolor2';

import {
	convertInputTimeToSeconds,
	convertSecondsToInputTime,
	getDivEmbedCode,
	getEmbedShareSettings,
	getIframeEmbedCode,
	getPreviewLink,
	getQplayerEmbedCode,
	joinClassNames,
} from '../../util/util';
import { Embedded, Email, Facebook, LinkedIn, X } from '../QPlayerSVGElement';
import { DateTimeHelper } from '../../util/date-time-helper';
import { useConfiguration } from '../../contexts/ConfigurationContext';
import { usePlayer } from '../../contexts/PlayerContext';

import ControlButton from './ControlButton';
import './ShareMediaDialog.scss';

const useStyles = makeStyles(() => ({
	selectedButton: (props: { color: string }) => ({
		border: "1px solid transparent !important",
		borderBottomColor: `${props.color} !important`,
		padding: "5px 15px !important",
		backgroundColor: 'transparent !important',
		color: "#ffffff !important",
	}),
	unselectedButton: {
		border: "1px solid transparent !important",
		borderBottomColor: 'rgba(255, 255, 255, 0.2) !important',
		padding: "5px 15px !important",
		backgroundColor: 'transparent !important',
		color: "#ffffff !important",
	},
}));

interface ShareMediaDialogProps {
	configs?: any;
	mediaDetails?: any;
	playerConfigs?: any;
	isMobile?: boolean;
	sideWindowOpen?: boolean;
}
const baseClassName = 'qplayer__sharing-dialog';

const ShareMediaDialog = ({ isMobile, playerConfigs }: ShareMediaDialogProps) => {
	const { t } = useTranslation();

	const { mediaName, accountId, mediaType, openSharingModal, showCountdownModule, embedShareSettings: embedShareSettingsMessage, isMediaLive, videoHoverUrl, configParams, currentMediaId, setOpenSharingModal } = useConfiguration();
	const { currentPosition } = usePlayer();

	const [selectedEmbedded, setSelectedEmbedded] = useState<'div' | 'iframe' | null>(null);
	const [startTimeChecked, setStartTimeChecked] = useState(false);
	const [startTime, setStartTime] = useState(currentPosition);
	const [timeInputValue, setTimeInputValue] = useState('');
	const [isCheckboxHovering, setCheckboxHovering] = useState(false);
	const isPlaylist = !!configParams.playlistId;
	// A reels feed defaults to the clip on screen: a playlist link would open whichever clip is first.
	const [sharePlaylistChecked, setSharePlaylistChecked] = useState(() => !isReelsFeedEnabled(configParams));
	const isSharingPlaylist = isPlaylist && (sharePlaylistChecked || !currentMediaId);

	const embedShareSettings = getEmbedShareSettings({ configParams, messageData: embedShareSettingsMessage });
	// A reels feed has nothing to offer either of these. The embed code describes a player this page
	// is not running - the feed is its own embed with its own attributes - and a start time belongs
	// to a clip the viewer scrolls away from, so a link carrying one reopens the feed part-way into
	// a video nobody chose.
	const isReelsFeed = isReelsFeedEnabled(configParams);
	const color = useMemo(() => playerConfigs?.playerStyling?.primaryColor ?? '#126cfc', [playerConfigs?.playerStyling?.primaryColor]);
	const classes = useStyles({ color });
	const primaryBGColor = useMemo(() => ({ backgroundColor: color }), [color]);
	const opacity = Number(playerConfigs?.playerStyling?.primaryOpacity ?? 100);
	const rgbaWithOpa = { ...tinycolor(color).toRgb(), a: opacity / 100 };
	const hoverStyle = isMobile ? {} : { backgroundColor: tinycolor(rgbaWithOpa).toRgbString() };

	const getEmbeddedValue = useCallback(() => {
		const params = {
			mediaDetails: {
				id: isSharingPlaylist
					? configParams.playlistId
					: isPlaylist
						? currentMediaId
						: isReelsFeedEnabled(configParams) ? currentMediaId || configParams.playlistId : configParams.scenarioId || configParams.mediaId,
				title: mediaName,
			},
			configs: { id: configParams.configId, ...playerConfigs },
			paramConfigs: configParams,
			accountId: accountId,
			type: isSharingPlaylist ? 'playlist' : mediaType,
			time: startTimeChecked ? startTime : undefined,
		};

		if (configParams.useLoader !== "true") {
			return getQplayerEmbedCode({ ...params, embedType: selectedEmbedded || 'div' });
		}
		return selectedEmbedded === 'div' ? getDivEmbedCode({ ...params }) : getIframeEmbedCode({ ...params });
	}, [accountId, mediaName, mediaType, playerConfigs, selectedEmbedded, startTime, startTimeChecked, configParams]);

	const getLink = () => {
		const params = {
			mediaDetails: {
				id: isSharingPlaylist
					? configParams.playlistId
					: isPlaylist
						? currentMediaId
						: isReelsFeedEnabled(configParams) ? currentMediaId || configParams.playlistId : configParams.scenarioId || configParams.mediaId,
				title: mediaName,
			},
			configs: { id: configParams.configId, ...playerConfigs },
			paramConfigs: configParams,
			accountId: accountId,
			type: isSharingPlaylist ? 'playlist' : mediaType,
			time: startTimeChecked ? startTime : undefined,
			embedShareSettings: embedShareSettings,
		};
		return getPreviewLink({ ...params });
	};

	const shareToFb = () => {
		const shareUrl = encodeURIComponent(getLink());
		const windowToOpen = `https://web.facebook.com/sharer/sharer.php?u=${shareUrl}&display=popup`;
		window.open(windowToOpen, 'facebookShare', 'popup,left=100,top=100,width=410,height=320');
	};

	const shareToX = () => {
		const shareUrl = encodeURIComponent(getLink());
		const windowToOpen = `https://twitter.com/intent/tweet?url=${shareUrl}`;
		window.open(windowToOpen, 'twtShare', 'popup,left=100,top=100,width=410,height=320');
	};

	const shareToLinkedin = () => {
		const shareUrl = encodeURIComponent(getLink());
		const windowToOpen = `https://www.linkedin.com/shareArticle?url=${shareUrl}&mini=true`;
		window.open(windowToOpen, 'linkedinShare', 'popup,left=100,top=100,width=410,height=320');
	};

	const mailTo = () => {
		const shareUrl = encodeURIComponent(getLink());
		window.open('mailto:?body=' + shareUrl);
	};

	const handleOnTimeCheckboxChange = (_event: React.ChangeEvent<{}>, checked: boolean) => {
		setStartTimeChecked(checked);
		checked && timeInputValue && setStartTime(convertInputTimeToSeconds(timeInputValue));
	};

	const handleCopyText = (textToCopy: string) => {
		navigator.clipboard.writeText(textToCopy);
	};

	const onTimeInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		if (isNaN(parseFloat(value))) {
			setStartTime(convertInputTimeToSeconds(value));
			return;
		}

		setTimeInputValue(value);
	}, []);

	const onSubmitStartTime = () => {
		const timeValue = startTimeChecked ? convertInputTimeToSeconds(timeInputValue) : '';
		setStartTime(timeValue);
	};

	const getTimeCheckboxLabel = () => {
		if (startTimeChecked) {
			return (
				<div
					style={{
						color: '#ffffffc7',
					}}
				>
					<span aria-hidden="true">{t('SHARE_DIALOG_START_AT_CHECKBOX_LABEL')}</span>
					<input
						className={`${baseClassName}__time-input`}
						onChange={onTimeInputChange}
						value={timeInputValue}
						onBlur={onSubmitStartTime}
						aria-label={`${t('SHARE_DIALOG_START_AT_CHECKBOX_LABEL')} ${DateTimeHelper.formatHumanizeDuration(timeInputValue, t) || `0 ${t('LABEL_SECOND')}`}`}
					/>
				</div>
			);
		}
		return (
			<div
				style={{
					color: '#ffffffc7',
				}}
				aria-label={`${t('SHARE_DIALOG_START_AT_CHECKBOX_LABEL')} ${DateTimeHelper.formatHumanizeDuration(timeInputValue, t) || `0 ${t('LABEL_SECOND')}`}`}
			>
				{t('SHARE_DIALOG_START_AT_CHECKBOX_LABEL')}
				{timeInputValue}
			</div>
		);
	};

	const closeShareModal = useCallback(() => {
		setOpenSharingModal(false);
		setTimeInputValue('');
		setStartTimeChecked(false);
		setSelectedEmbedded(null);
		setStartTime(currentPosition);
		setCheckboxHovering(false);
		setSharePlaylistChecked(!isReelsFeedEnabled(configParams));

		const container = !configParams?.qplayerPreview ? configParams?.container ?? document : document;
		const anchorElem = container.querySelector('.qplayer_share_btn');
		anchorElem && anchorElem.focus()
	}, [configParams, currentPosition]);

	useEffect(() => {
		timeInputValue === '' && openSharingModal && setTimeInputValue(convertSecondsToInputTime(currentPosition));
	}, [openSharingModal, currentPosition, timeInputValue]);

	return (
		openSharingModal ? (
			<div
				className={joinClassNames("qplayer-dialog", baseClassName)}
				onKeyDown={(e) => {
					if (e.key === 'Escape') {
						closeShareModal();
					}
				}}
			>
				<div className={"qplayer-dialog__content"} role="dialog" aria-labelledby="share-dialog-title" aria-modal="true">
					<div className={`${baseClassName}__title qplayer-semibold-font`}>
						{selectedEmbedded && (
							<ControlButton
								tabIndex={0}
								icon={<ArrowBack htmlColor="currentColor" />}
								onClick={() => setSelectedEmbedded(null)}
								ariaLabel={t('SHARE_DIALOG_BACK')}
								tooltip={t('SHARE_DIALOG_BACK')}
								hoverStyle={hoverStyle}
								topControl
								aria-hidden={selectedEmbedded ? 'false' : 'true'}
								style={selectedEmbedded ? {} : { visibility: 'hidden' }}
								tooltipStyle={{ top: '40px' }}
							/>
						)}

						<span tabIndex={-1} aria-label={`${t('SHARE_DIALOG_TITLE')}. ${isMobile ? '' : t('SHARE_DIALOG')}`} id="share-dialog-title">
							<span tabIndex={-1} aria-hidden="true" role="heading" aria-level={1}>
								{t('SHARE_DIALOG_TITLE')}
							</span>
						</span>

						<ControlButton
							tabIndex={0}
							icon={<Close htmlColor="currentColor" />}
							onClick={closeShareModal}
							ariaLabel={t('SHARE_DIALOG_CLOSE')}
							tooltip={t('SHARE_DIALOG_CLOSE')}
							hoverStyle={hoverStyle}
							topControl
							tooltipStyle={{ top: '40px' }}
							autoFocus
						/>
					</div>

					<div className={`${baseClassName}__content`}>
						{selectedEmbedded ? (
							<div
								className={`${baseClassName}__content-section ${baseClassName}__content-section-embedded ${baseClassName}__input`}
								style={{ display: 'flex', flexDirection: 'column-reverse', position: 'relative' }}
							>
								<div className={`${baseClassName}__content-action`}>
									<ButtonGroup>
										<Button
											role="tab"
											aria-selected={selectedEmbedded === 'div' ? 'true' : 'false'}
											aria-controls="tabpanel-div"
											className={joinClassNames(`${baseClassName}__tab`, selectedEmbedded === 'div' ? classes.selectedButton : classes.unselectedButton)}
											onClick={() => setSelectedEmbedded('div')}
											aria-label={t('SHARE_DIALOG_DIV_EMBED')}
										>
											<span aria-hidden="true">div</span>
										</Button>
										<Button
											role="tab"
											aria-selected={selectedEmbedded === 'iframe' ? 'true' : 'false'}
											aria-controls="tabpanel-iframe"
											className={joinClassNames(`${baseClassName}__tab`, selectedEmbedded === 'iframe' ? classes.selectedButton : classes.unselectedButton)}
											onClick={() => setSelectedEmbedded('iframe')}
											aria-label={t('SHARE_DIALOG_IFRAME_EMBED')}
										>
											<span aria-hidden="true">iframe</span>
										</Button>
									</ButtonGroup>
								</div>
								<TextareaAutosize
									role="tabpanel"
									id={selectedEmbedded === 'iframe' ? 'tabpanel-iframe' : 'tabpanel-div'}
									readOnly
									value={getEmbeddedValue()}
									aria-label={t('SHARE_DIALOG_SHARE_EMBED')}
								/>
								<Button
									className="share-dialog-copy-button"
									variant="contained"
									color="primary"
									size="small"
									aria-label={t(selectedEmbedded === 'div' ? 'SHARE_DIALOG_COPY_EMBED_DIV' : 'SHARE_DIALOG_COPY_EMBED_IFRAME')}
									onClick={() => handleCopyText(getEmbeddedValue())}
									style={{
										...primaryBGColor,
										width: '52px',
										height: '26px',
										padding: '4px 10px',
										minWidth: '52px',
										position: 'absolute',
										right: '12px',
										boxSizing: 'content-box',
									}}
								>
									{t('LABEL_COPY_BUTTON')}
								</Button>
							</div>
						) : (
							<>
								<div
									className={`${baseClassName}__content-section ${baseClassName}__content-section--no-space`}
									style={{ display: 'flex' }}
								>
									{!isReelsFeed && (
										<ControlButton
											tabIndex={0}
											icon={<Embedded color="currentColor" />}
											onClick={() => setSelectedEmbedded('div')}
											ariaLabel={t('SHARE_DIALOG_OPEN_EMBED_ARIA_LABEL')}
											tooltip={t('SHARE_DIALOG_OPEN_EMBED')}
											hoverStyle={hoverStyle}
											topControl
											tooltipStyle={{ top: '-30px', left: '-15px' }}
										/>
									)}
									<ControlButton
										tabIndex={0}
										icon={<Facebook color="currentColor" />}
										onClick={shareToFb}
										ariaLabel={t('SHARE_DIALOG_SHARE_TO_FACEBOOK_ARIA_LABEL')}
										tooltip={t('SHARE_DIALOG_SHARE_TO_FACEBOOK')}
										hoverStyle={hoverStyle}
										topControl
										tooltipStyle={{ top: '-30px' }}
									/>
									<ControlButton
										tabIndex={0}
										icon={<X color="currentColor" />}
										onClick={shareToX}
										ariaLabel={t('SHARE_DIALOG_SHARE_TO_X_ARIA_LABEL')}
										tooltip={t('SHARE_DIALOG_SHARE_TO_X')}
										hoverStyle={hoverStyle}
										topControl
										tooltipStyle={{ top: '-30px' }}
									/>
									<ControlButton
										tabIndex={0}
										icon={<LinkedIn color="currentColor" />}
										onClick={shareToLinkedin}
										ariaLabel={t('SHARE_DIALOG_SHARE_TO_LINKEDIN_ARIA_LABEL')}
										tooltip={t('SHARE_DIALOG_SHARE_TO_LINKEDIN')}
										hoverStyle={hoverStyle}
										topControl
										tooltipStyle={{ top: '-30px' }}
									/>
									<ControlButton
										tabIndex={0}
										icon={<Email color="currentColor" />}
										onClick={mailTo}
										ariaLabel={t('SHARE_DIALOG_SHARE_TO_EMAIL_ARIA_LABEL')}
										tooltip={t('SHARE_DIALOG_SHARE_TO_EMAIL')}
										hoverStyle={hoverStyle}
										topControl
										tooltipStyle={{ top: '-30px' }}
									/>

									{/* Not in a feed. The clip on screen is what a viewer means by "this",
									    and a playlist link opens whichever clip happens to be first - so
									    the box is only ever the wrong answer here, which is why it
									    already defaults to unchecked. Same reasoning as the embed and
									    start-at controls above. */}
									{isPlaylist && !isReelsFeed && (
										<FormControlLabel
											control={
												<Checkbox
													onChange={(_event, checked) => setSharePlaylistChecked(checked)}
													checked={sharePlaylistChecked}
													color={'primary'}
													style={{ color: '#ffffff', padding: '2px', marginRight: '2px' }}
												/>
											}
											label={t('SHARE_DIALOG_SHARE_PLAYLIST_CHECKBOX_LABEL')}
											labelPlacement='start'
											style={{ color: '#ffffffc7', marginLeft: 'auto', marginRight: 0 }}
										/>
									)}
								</div>

								<OutlinedInput
									className={`${baseClassName}__content-section ${baseClassName}__input`}
									endAdornment={
										<InputAdornment position="end">
											<Button
												className="share-dialog-copy-button"
												variant="contained"
												color="primary"
												size="small"
												style={{ ...primaryBGColor, width: '52px', height: '26px', padding: '4px 10px', minWidth: '52px', boxSizing: 'content-box' }}
												aria-label={t('SHARE_DIALOG_COPY_LINK')}
												onClick={() => handleCopyText(getLink())}
											>
												{t('LABEL_COPY_BUTTON')}
											</Button>
										</InputAdornment>
									}
									value={getLink()}
									onChange={(_event) => { }}
									fullWidth
									readOnly
									aria-label={t('SHARE_DIALOG_SHARE_LINK')}
								/>
							</>
						)}

						{!showCountdownModule && mediaType === 'video' && !isMediaLive && videoHoverUrl && !isReelsFeed && (
							<div className={`${baseClassName}__content-section ${baseClassName}__content-section--flex`}>
								<Checkbox
									inputProps={{
										'aria-label': startTimeChecked
											? t('SHARE_DIALOG_START_AT_CHECKBOX_ARIA_LABEL_DEACTIVATED')
											: `${t('SHARE_DIALOG_START_AT_CHECKBOX_ARIA_LABEL')} ${t('LABEL_AT')} ${DateTimeHelper.formatHumanizeDuration(timeInputValue, t) || `0 ${t('LABEL_SECOND')}`}`,
									}}
									onChange={handleOnTimeCheckboxChange}
									checked={startTimeChecked}
									color={'primary'}
									style={
										isCheckboxHovering
											? { ...hoverStyle, color: '#ffffff', padding: '2px', marginRight: '2px' }
											: { color: '#ffffff', padding: '2px', marginRight: '2px' }
									}
									onMouseEnter={() => setCheckboxHovering(true)}
									onMouseLeave={() => setCheckboxHovering(false)}
									onFocus={() => setCheckboxHovering(true)}
									onBlur={() => setCheckboxHovering(false)}
								/>
								<div
									aria-hidden="true"
									className={joinClassNames([
										'controlbar__control-tooltip',
										'controlbar__control-tooltip--light',
										isCheckboxHovering ? 'controlbar__control-tooltip--visible' : '',
									])}
									style={{ top: '-30px', left: '-15px' }}
								>
									<div className="controlbar__control-tooltip-text">
										{startTimeChecked
											? t('SHARE_DIALOG_START_AT_CHECKBOX_ARIA_LABEL_DEACTIVATED')
											: `${t('SHARE_DIALOG_START_AT_CHECKBOX_ARIA_LABEL')} ${t('LABEL_AT')} ${DateTimeHelper.formatHumanizeDuration(timeInputValue, t) || `0 ${t('LABEL_SECOND')}`}`}
									</div>
								</div>
								<FormLabel>{getTimeCheckboxLabel()}</FormLabel>
							</div>
						)}
					</div>
				</div>
			</div>
		) : null
	);
};

export default ShareMediaDialog;
